import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { defineMessages } from 'react-intl';
import Isvg from 'react-inlinesvg';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Confirm from 'components/Confirm';
import { Form, RadioGroup, RadioButton } from 'components/HookForm';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import { SMARTSPOT_MODE, SMARTSPOT_REASON } from 'constants/logic';
import { PFE_ICONS } from 'constants/icons';

import appMessages from 'features/App/App.messages';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { selectActiveVenueId } from 'selectors/general';

import './ManageSmartSpotModal.scss';
import When from 'components/When';

const messages = defineMessages({
  allFuture: {
    id: 'yield-management.smartspot.manage-smartspot-modal.reason-all-future',
    defaultMessage: 'This instance and all upcoming instances',
  },
  thisInstance: {
    id: 'yield-management.smartspot.manage-smartspot-modal.reason-this-instance',
    defaultMessage: 'This instance only',
  },
  turnSmartSpotOn: {
    id: 'yield-management.smartspot.manage-smartspot-modal.turn-smart-spot-on',
    defaultMessage: 'Turn SmartSpot ON for:',
  },
  turnSmartSpotOff: {
    id: 'yield-management.smartspot.manage-smartspot-modal.turn-smart-spot-off',
    defaultMessage: 'Are you sure you want to disable SmartSpot for this schedule?',
  },
  confirmEnable: {
    id: 'yield-management.smartspot.manage-smartspot-modal.confirm-enable',
    defaultMessage: 'Enable SmartSpot',
  },
  confirmDisable: {
    id: 'yield-management.smartspot.manage-smartspot-modal.confirm-disable',
    defaultMessage: 'Disable SmartSpot',
  },
  disclaimerTitle: {
    id: 'yield-management.smartspot.manage-smartspot-modal.disclaimer-title',
    defaultMessage: 'SmartSpot is ensuring your prime spots are always protected for direct clients.',
  },
  disclaimerText: {
    id: 'yield-management.smartspot.manage-smartspot-modal.disclaimer-text',
    defaultMessage:
      '<strong>Want to release more spots?</strong> You can increase them without disabling SmartSpot by adjusting your SmartSpot settings <button>here</button>.',
  },
});

const FORM_NAME = 'manage-smartspot-form';

const { block, element } = bem('manage-smartspot-modal');

const ManageSmartSpotModal = ({ initialValues, onSubmit, smartspotEnabledForClass, canOnlyToggleNonRecurring }) => {
  const titleMessage = smartspotEnabledForClass ? messages.turnSmartSpotOn : messages.turnSmartSpotOff;
  const confirmMessage = smartspotEnabledForClass ? messages.confirmEnable : messages.confirmDisable;

  const venueId = useSelector(selectActiveVenueId);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleAdjustSmartSpotSettings = () => {
    const pathToRedirect = makePath.smartTools(venueId);
    Segment.event('SmartSpot Toggle Modal Liquidation', { venueId });
    dispatch(closeModal());
    navigate(pathToRedirect);
  };

  return (
    <Form name={FORM_NAME} defaultValues={initialValues}>
      {({ handleSubmit }) => {
        return (
          <Confirm
            className={block()}
            title={<Message message={titleMessage} />}
            cancelNode={<Message message={appMessages.cancel} />}
            acceptNode={<Message message={confirmMessage} />}
            onConfirm={handleSubmit(onSubmit)}
            size="extended">
            {({ title, buttons, bodyClass }) => (
              <>
                {title}
                <div className={classNames(bodyClass, element('content'))}>
                  <When predicate={!canOnlyToggleNonRecurring}>
                    <RadioGroup name="reason">
                      <RadioButton value={SMARTSPOT_REASON.SCHEDULE_SETTING}>
                        <Message message={messages.thisInstance} />
                      </RadioButton>
                      <RadioButton value={SMARTSPOT_REASON.RECURRING_RULE}>
                        <Message message={messages.allFuture} />
                      </RadioButton>
                    </RadioGroup>
                  </When>

                  <When predicate={!smartspotEnabledForClass}>
                    <div className={element('disclaimer-block')}>
                      <div className="icon">
                        <Isvg src={PFE_ICONS.smartSpotShieldOutline} />
                      </div>
                      <Message as="h6" message={messages.disclaimerTitle} />
                      <Message
                        message={messages.disclaimerText}
                        values={{
                          button: text => (
                            <button type="button" onClick={handleAdjustSmartSpotSettings}>
                              {text}
                            </button>
                          ),
                          strong: text => <strong>{text}</strong>,
                        }}
                      />
                    </div>
                  </When>
                </div>
                {buttons}
              </>
            )}
          </Confirm>
        );
      }}
    </Form>
  );
};

ManageSmartSpotModal.propTypes = {
  initialValues: PropTypes.shape({
    smartspotEnabled: PropTypes.bool,
    mode: PropTypes.oneOf(Object.values(SMARTSPOT_MODE)),
    reason: PropTypes.oneOf(Object.values(SMARTSPOT_REASON)),
  }),
  onSubmit: PropTypes.func,
  smartspotEnabledForClass: PropTypes.bool,
  canOnlyToggleNonRecurring: PropTypes.bool,
};

export default ManageSmartSpotModal;
